import axios from 'axios';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authApi, User, AuthResponse, RegisterResponse } from '../api/auth.api';
import { registerWebPushSubscription } from '../services/push.service';
import { getNotificationPermission } from '../utils/notifications';

function syncWebPushAfterAuth(): void {
  if (getNotificationPermission() === 'granted') {
    registerWebPushSubscription().catch(() => {});
  }
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  requires2fa: boolean;
  login: (data: { email: string; password: string; 'cf-turnstile-response'?: string }) => Promise<AuthResponse>;
  register: (data: any) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  fetchUser: () => Promise<void>;
  setAuth: (user: User, token: string) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      requires2fa: false,

      login: async (data: { email: string; password: string; 'cf-turnstile-response'?: string }) => {
        const response = await authApi.login(data);
        if (response.requires_2fa && response.token) {
          set({ requires2fa: true, token: response.token });
          localStorage.setItem('token', response.token);
          return response;
        }
        set({
          user: response.user,
          token: response.token,
          isAuthenticated: true,
          requires2fa: false,
        });
        localStorage.setItem('token', response.token);
        syncWebPushAfterAuth();
        return response;
      },

      register: async (data: any) => {
        const response = await authApi.register(data);
        if (response.token && response.user) {
          set({
            user: response.user,
            token: response.token,
            isAuthenticated: true,
          });
          localStorage.setItem('token', response.token);
          syncWebPushAfterAuth();
        }
        return response;
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch (_) {
          // ignore logout errors
        }
        set({
          user: null,
          token: null,
          isAuthenticated: false,
          requires2fa: false,
        });
        localStorage.removeItem('token');
      },

      fetchUser: async () => {
        try {
          const user = await authApi.me();
          set({ user, isAuthenticated: true });
        } catch (error) {
          // Keep session on network/CORS/timeouts — only clear on explicit 401.
          if (axios.isAxiosError(error) && error.response?.status === 401) {
            set({ user: null, token: null, isAuthenticated: false });
            localStorage.removeItem('token');
          }
        }
      },

      setAuth: (user: User, token: string) => {
        set({ user, token, isAuthenticated: true, requires2fa: false });
        syncWebPushAfterAuth();
        localStorage.setItem('token', token);
      },

      clearAuth: () => {
        set({ user: null, token: null, isAuthenticated: false, requires2fa: false });
        localStorage.removeItem('token');
      },
    }),
    {
      name: 'auth-storage',
    }
  )
);
